import { CircularProgress, Input, makeStyles } from '@material-ui/core';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  memo, useEffect, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import _ from 'lodash';
import { availableCabinsRequest, getSpecificCabinSuccess, resetGetSpecificCabinSearchStatus } from '../../../../../../store/slices/cruise';
import { SearchStatus } from '../../../../../../constants';
import AddButton from '../../../../../AddButton';
import ErrorTooltip from '../../../../../ErrorTooltip';
import formatStateroomsForRequest from '../../../../../../helpers/formatters/formatStateroomsForRequest';

const cabinInputStyles = makeStyles(() => ({
  input: {
    maxWidth: 'fit-content',
  },
}));

/*
TODO: since the custom select is similar to the main cabin select row what if we refactor
and make the insides of the tbody a component and reuse that component within the custom select
https://bitbucket.org/travelwits/cruises_frontend/pull-requests/110/cruis-327-select-specific-cabin#comment-215262765
*/

const SpecificCabinSelect = ({
  checkIsSelected, onSelect, onRemoveFromTrip,
  itineraryId, cabinCategoryCode, cabinCategoryRateOptionCode,
  onCabinDeckNameClick, isSelectedRateOption, selectedRoomIndex,
  gradeno, resultno, sessionKey,
}) => {
  const {
    currentTripId,
    getSpecificCabinSearchStatus,
    specificCabin,
    availableCabins,
  } = useSelector((state) => state.cruise);

  const { staterooms } = useSelector((state) => state.tripSearch.searchObj);
  const doAllowSelectCabin = onSelect !== null;
  const selectedTrip = useSelector((state) => state.trip.selectedTrips[currentTripId]);
  const cruiseDetails = useSelector((state) => state.cruise.cruiseDetails);
  const [hasError, setHasError] = useState(false);

  const cabinInputClasses = cabinInputStyles();
  const textRef = useRef();
  const dispatch = useDispatch();

  useEffect(() => {
    if (availableCabins && isSelectedRateOption && selectedTrip && selectedTrip.cruiseItinerary
      && selectedTrip.cruiseItinerary.cabins
      && selectedTrip.cruiseItinerary.cabins[selectedRoomIndex]) {
      const selectedCabin = availableCabins.find((cabin) => checkIsSelected(cabin));
      if (selectedCabin == null && !specificCabin) {
        textRef.current.value = selectedTrip.cruiseItinerary
          .cabins[selectedRoomIndex].selectedShipCabin.cabinNumber;
        dispatch(getSpecificCabinSuccess({
          cabin: selectedTrip.cruiseItinerary.cabins[selectedRoomIndex].selectedShipCabin,
        }));
        dispatch(resetGetSpecificCabinSearchStatus());
      }
    }

    if (getSpecificCabinSearchStatus === SearchStatus.DONE) {
      if (specificCabin) {
        setHasError(false);
        if (doAllowSelectCabin) {
          onSelect(specificCabin);
        }
      } else {
        setHasError(true);
      }
      dispatch(resetGetSpecificCabinSearchStatus());
    }
  }, [getSpecificCabinSearchStatus, dispatch, onSelect, availableCabins,
    selectedTrip, checkIsSelected, specificCabin, isSelectedRateOption,
    doAllowSelectCabin, selectedRoomIndex]);

  const handleFindCabinClick = () => {
    const cabinNumber = textRef.current.value;
    if (cabinNumber == null || cabinNumber.trim() === '') {
      setHasError(true);
    } else {
      const cabins = selectedTrip && selectedTrip.cruiseItinerary
      && selectedTrip.cruiseItinerary.cabins;
      if (_.find(cabins, (c) => c && c.selectedShipCabin
      && c.selectedShipCabin.cabinNumber === cabinNumber)) {
        setHasError(true);
      } else {
        dispatch(availableCabinsRequest({
          itineraryId,
          cabinCategoryCode,
          cabinCategoryRateOptionCode,
          cabinNumber,
          ...formatStateroomsForRequest(staterooms[selectedRoomIndex],
            cruiseDetails && cruiseDetails.cruiseLine.cruiseLineId),
          gradeno,
          resultno,
          sessionKey,
        }));
      }
    }
  };

  return (
    <>
      <tr>
        <td className="px-5 pt-5 pb-3 font-bold" colSpan="5">
          If you want to check a specific cabin&apos;s availability,
          please enter the cabin number below and click &quot;Find & Select&quot; button
        </td>
      </tr>
      <tr className={`h-16 md:border-b-2 cursor-default ${clsx({ 'bg-theme bg-opacity-10': (checkIsSelected(specificCabin || {})) })}`}>
        <td className="p-5">
          <ErrorTooltip
            title="Cabin is unavailable."
            open={hasError}
            arrow
            PopperProps={{ disablePortal: true }}
          >
            <Input
              fullWidth
              classes={cabinInputClasses}
              placeholder="Cabin #"
              inputRef={textRef}
              error={hasError}
              onChange={() => setHasError(false)}
            />
          </ErrorTooltip>
        </td>
        <td className="p-2">
          {specificCabin ? specificCabin.cabinBedTypeOptions.join(', ') : 'N/A'}
        </td>
        <td className="p-2">
          {specificCabin ? (
            <a
              className="text-theme underline cursor-pointer"
              role="button"
              tabIndex="-1"
              onClick={
                onCabinDeckNameClick(specificCabin.cabinDeckName)
              }
              onKeyDown={
                onCabinDeckNameClick(specificCabin.cabinDeckName)
              }
            >
              {specificCabin.cabinDeckName}
            </a>
          ) : 'N/A'}
        </td>
        <td colSpan="2" className={clsx('p-2')}>
          <div className="flex justify-between items-center ">
            {specificCabin && specificCabin.cabinLocations ? specificCabin.cabinLocations.join(', ') : 'N/A'}
            <div className="hidden md:block">
              {getSpecificCabinSearchStatus === SearchStatus.SEARCHING
                && <CircularProgress color="secondary" size={30} className="align-middle" />}
              <AddButton
                disabled={getSpecificCabinSearchStatus === SearchStatus.SEARCHING}
                isSelected={checkIsSelected(specificCabin || {})}
                onAdd={() => handleFindCabinClick()}
                onRemove={() => onRemoveFromTrip()}
                addLabel={doAllowSelectCabin ? 'Find & Select' : 'Find'}
                removeLabel="Remove"
              />
            </div>
          </div>
        </td>
      </tr>
      <tr className="md:hidden">
        <td colSpan="5" className="pl-2 pr-5 text-right ">
          {getSpecificCabinSearchStatus === SearchStatus.SEARCHING
            && <CircularProgress color="secondary" size={30} className="align-middle" />}
          <div className="mb-2">
            <AddButton
              disabled={getSpecificCabinSearchStatus === SearchStatus.SEARCHING}
              isSelected={checkIsSelected(specificCabin || {})}
              onAdd={() => handleFindCabinClick()}
              onRemove={() => onRemoveFromTrip(selectedRoomIndex)}
              addLabel={doAllowSelectCabin ? 'Find & Select' : 'Find'}
              removeLabel="Remove"
            />
          </div>
        </td>
      </tr>
    </>
  );
};
SpecificCabinSelect.propTypes = {
  checkIsSelected: PropTypes.func.isRequired,
  onRemoveFromTrip: PropTypes.func.isRequired,
  onSelect: PropTypes.func,
  itineraryId: PropTypes.string.isRequired,
  cabinCategoryCode: PropTypes.string.isRequired,
  cabinCategoryRateOptionCode: PropTypes.string.isRequired,
  onCabinDeckNameClick: PropTypes.func.isRequired,
  isSelectedRateOption: PropTypes.bool,
  selectedRoomIndex: PropTypes.number.isRequired,
  gradeno: PropTypes.string.isRequired,
  resultno: PropTypes.string.isRequired,
  sessionKey: PropTypes.string.isRequired,
};

SpecificCabinSelect.defaultProps = {
  isSelectedRateOption: false,
  onSelect: undefined,
};

export default memo(SpecificCabinSelect);
